/* eslint-disable react/prop-types */
import ReactFlow, { ReactFlowProvider, useReactFlow } from "reactflow";
import { Typography, IconButton, Box } from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useState, useEffect } from "react";
import useEditWorkflowData from "./useEditNewWorkflowData";
import "reactflow/dist/style.css";
import { useTheme, lighten, darken } from "@mui/material/styles";
import { PtwSettingsData } from "./workFlow-context";
import Toast from "@/components/toast";
import { useTranslation } from "react-i18next";
import EditWorkflowUserGuide from "@/components/dialogs/EditWorkflowUserGuide";
import StepModalDialog from "./components/StepModalDialog";
import DeleteStepInputDialog from "./components/DeleteStepInputDialog";

// Child component to handle ReactFlow and fitView logic
const FlowContainer = ({ nodes, edges, onNodesChange, isDarkMode, t }) => {
  const { fitView } = useReactFlow();
  const theme = useTheme();
  // Fit the view whenever nodes or edges change
  useEffect(() => {
    if (nodes.length > 0) {
      const timeout = setTimeout(() => {
        fitView({ padding: 0.1, duration: 200 });
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [nodes, edges, fitView]);

  return (
    <>
      <Typography>{t("editWorkFlow.title")}</Typography>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        defaultEdgeOptions={{
          animated: true,
          style: {
            stroke: theme.palette.primary.dark,
            strokeWidth: 2,
          },
        }}
        fitView
        fitViewOptions={{ padding: 0.1 }}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 8,
          border: `1px solid ${
            isDarkMode
              ? lighten(theme.palette.divider, 0.8)
              : darken(theme.palette.divider, 0.5)
          }`,
        }}
        aria-label={t("editWorkFlow.flowAria")}
      />
    </>
  );
};

const EditWorkFlow = () => {
  const { t } = useTranslation();
  const {
    nodeData,
    setNodeData,
    nodes,
    onNodesChange,
    edges,
    isModalOpen,
    userGroupIds,
    closeModal,
    handleMultiChange,
    handleUpdateNodeData,
    toast,
    handleToastClose,
    inputTypeList,
    handleInputTypeChange,
    addNewStepInput,
    removeStepInput,
    moveStepInputUp,
    moveStepInputDown,
    addOption,
    removeOption,
    userGroupsList,
  } = useEditWorkflowData();

  const theme = useTheme();
  const { goBackToList } = PtwSettingsData();
  const [isFormValid, setIsFormValid] = useState(false);
  const [optionInputs, setOptionInputs] = useState({});
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    stepIndex: null,
    stepName: "",
  });
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const isDarkMode = theme.palette.mode === "dark";
  // Dynamic toolbar height with extra spacing for app bar shadows/styling
  const toolbarHeight = (theme.mixins.toolbar.minHeight || 64) + 16;

  // Validate all required fields, including step_inputs and options
  useEffect(() => {
    const validateForm = () => {
      const staticFieldsValid =
        userGroupIds.length > 0 &&
        Boolean(nodeData.action_name) &&
        Boolean(nodeData.status);

      if (!staticFieldsValid) return false;

      if (
        !Array.isArray(nodeData.step_inputs) ||
        nodeData.step_inputs.length === 0
      ) {
        return true;
      }

      const stepInputsValid = nodeData.step_inputs.every((step_input) => {
        if (!step_input.input_type_id || !step_input.name) {
          return false;
        }

        const inputType = inputTypeList.find(
          (option) => option.value === step_input.input_type_id
        );
        if (
          inputType &&
          ["selection", "multi_selection"].includes(inputType.input_key) &&
          (!step_input.options || step_input.options.length === 0)
        ) {
          return false;
        }

        return true;
      });

      return staticFieldsValid && stepInputsValid;
    };

    setIsFormValid(validateForm());
  }, [
    userGroupIds.length,
    nodeData.action_name,
    nodeData.status,
    nodeData.step_inputs,
    inputTypeList,
  ]);

  const handleOptionInputChange = (index, value) => {
    setOptionInputs((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  const handleAddOption = (index) => {
    const optionInput = optionInputs[index] || "";
    if (!optionInput.trim()) return;

    addOption(index, optionInput);
    setOptionInputs((prev) => ({
      ...prev,
      [index]: "",
    }));
  };

  const handleDeleteStepInput = (index) => {
    const stepName =
      nodeData.step_inputs?.[index]?.name ||
      `${t("editWorkFlow.stepInput")} ${index + 1}`;
    setDeleteConfirmation({
      open: true,
      stepIndex: index,
      stepName: stepName,
    });
  };

  const confirmDeleteStepInput = () => {
    const index = deleteConfirmation.stepIndex;
    removeStepInput(index);
    setDeleteConfirmation({ open: false, stepIndex: null, stepName: "" });
  };

  const cancelDeleteStepInput = () => {
    setDeleteConfirmation({ open: false, stepIndex: null, stepName: "" });
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          height: `calc(100vh - ${toolbarHeight}px - 32px)`,
          width: "100%",
          position: "relative",
          overflow: "hidden",
          mt: `${toolbarHeight}px`,
          p: 0,
          m: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: "100%",
            height: "100%",
            position: "relative",
            p: 0,
            m: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 32,
              left: 16,
              zIndex: 1000,
              display: "flex",
              gap: 1,
            }}
          >
            <IconButton
              sx={{
                bgcolor: theme.palette.background.paper,
                boxShadow: theme.shadows[2],
                "&:hover": {
                  bgcolor: theme.palette.background.paper,
                  boxShadow: theme.shadows[4],
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
              onClick={goBackToList}
              aria-label={t("editWorkFlow.backButtonAria")}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              sx={{
                bgcolor: theme.palette.info.main,
                color: theme.palette.info.contrastText,
                boxShadow: theme.shadows[2],
                "&:hover": {
                  bgcolor: theme.palette.info.dark,
                  boxShadow: theme.shadows[4],
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
              onClick={() => setHelpModalOpen(true)}
              aria-label={t("editWorkFlow.helpButtonAria")}
            >
              <HelpOutline />
            </IconButton>
          </Box>

          <ReactFlowProvider>
            <FlowContainer
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              isDarkMode={isDarkMode}
              t={t}
            />
          </ReactFlowProvider>
        </Box>
      </Box>

      <StepModalDialog
        open={isModalOpen}
        onClose={closeModal}
        modalButton="update"
        nodeData={nodeData}
        setNodeData={setNodeData}
        userGroupIds={userGroupIds}
        userGroupsList={userGroupsList}
        inputTypeList={inputTypeList}
        handleMultiChange={handleMultiChange}
        isFormValid={isFormValid}
        onSave={handleUpdateNodeData}
        onAddStep={null}
        handleInputTypeChange={handleInputTypeChange}
        addNewStepInput={addNewStepInput}
        handleDeleteStepInput={handleDeleteStepInput}
        moveStepInputUp={moveStepInputUp}
        moveStepInputDown={moveStepInputDown}
        optionInputs={optionInputs}
        handleOptionInputChange={handleOptionInputChange}
        handleAddOption={handleAddOption}
        removeOption={removeOption}
        translationPrefix="editWorkFlow"
      />

      {/* User Guide Help Modal */}
      <EditWorkflowUserGuide
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />

      <DeleteStepInputDialog
        open={deleteConfirmation.open}
        onClose={cancelDeleteStepInput}
        onConfirm={confirmDeleteStepInput}
        stepName={deleteConfirmation.stepName}
        translationPrefix="editWorkFlow"
      />

      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </>
  );
};

export default EditWorkFlow;
